// Valid email providers
const VALID_EMAIL_PROVIDERS = [
  'gmail.com',
  'yahoo.com',
  'hotmail.com',
  'outlook.com',
  'live.com',
  'msn.com',
  'aol.com',
  'icloud.com',
  'mail.com',
  'protonmail.com',
  'zoho.com',
  'yandex.com',
  'gmx.com',
  'rediffmail.com',
  'mail.ru',
  'qq.com',
  '163.com',
  'sina.com',
  'sohu.com',
  'foxmail.com',
  'me.com',
  'mac.com',
  'inbox.com',
  'fastmail.com',
  'hushmail.com',
  'tutanota.com',
  'riseup.net',
  'disroot.org',
  'cock.li',
  'pm.me',
  'proton.me',
  'hey.com',
  'custom', // Allow custom domains
]

// Valid top-level domains (TLDs)
const VALID_TLDS = [
  // Generic TLDs
  'com',
  'org',
  'net',
  'edu',
  'gov',
  'mil',
  'int',
  // Country code TLDs
  'pk', // Pakistan
  'co', // Colombia
  'uk', // United Kingdom
  'us', // United States
  'ca', // Canada
  'au', // Australia
  'de', // Germany
  'fr', // France
  'it', // Italy
  'es', // Spain
  'nl', // Netherlands
  'be', // Belgium
  'ch', // Switzerland
  'at', // Austria
  'se', // Sweden
  'no', // Norway
  'dk', // Denmark
  'fi', // Finland
  'pl', // Poland
  'cz', // Czech Republic
  'ie', // Ireland
  'pt', // Portugal
  'gr', // Greece
  'ro', // Romania
  'hu', // Hungary
  'bg', // Bulgaria
  'hr', // Croatia
  'si', // Slovenia
  'sk', // Slovakia
  'lt', // Lithuania
  'lv', // Latvia
  'ee', // Estonia
  'ru', // Russia
  'ua', // Ukraine
  'by', // Belarus
  'kz', // Kazakhstan
  'ge', // Georgia
  'am', // Armenia
  'az', // Azerbaijan
  'tr', // Turkey
  'il', // Israel
  'sa', // Saudi Arabia
  'ae', // UAE
  'eg', // Egypt
  'za', // South Africa
  'ng', // Nigeria
  'ke', // Kenya
  'ma', // Morocco
  'tn', // Tunisia
  'dz', // Algeria
  'in', // India
  'cn', // China
  'jp', // Japan
  'kr', // South Korea
  'tw', // Taiwan
  'hk', // Hong Kong
  'sg', // Singapore
  'my', // Malaysia
  'th', // Thailand
  'ph', // Philippines
  'id', // Indonesia
  'vn', // Vietnam
  'nz', // New Zealand
  'mx', // Mexico
  'br', // Brazil
  'ar', // Argentina
  'cl', // Chile
  'co', // Colombia
  'pe', // Peru
  've', // Venezuela
  'ec', // Ecuador
  'uy', // Uruguay
  'py', // Paraguay
  'bo', // Bolivia
  'cr', // Costa Rica
  'pa', // Panama
  'gt', // Guatemala
  'hn', // Honduras
  'ni', // Nicaragua
  'sv', // El Salvador
  'do', // Dominican Republic
  'cu', // Cuba
  'jm', // Jamaica
  'tt', // Trinidad and Tobago
  'bb', // Barbados
  'bz', // Belize
  'gy', // Guyana
  'sr', // Suriname
  // New gTLDs
  'io',
  'ai',
  'app',
  'dev',
  'tech',
  'online',
  'site',
  'website',
  'store',
  'shop',
  'blog',
  'info',
  'biz',
  'name',
  'me',
  'tv',
  'cc',
  'ws',
  'mobi',
  'asia',
  'tel',
  'jobs',
  'travel',
  'xxx',
  'xxx',
  'pro',
  'museum',
  'coop',
  'aero',
  'cat',
  'xxx',
]

// Enhanced email regex pattern
const EMAIL_REGEX = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)+$/

/**
 * Validates email format, provider, and TLD
 * @param {string} email - Email address to validate
 * @returns {object} - { isValid: boolean, error: string }
 */
export const validateEmail = (email) => {
  if (!email || typeof email !== 'string') {
    return { isValid: false, error: 'Email is required' }
  }

  const trimmedEmail = email.trim().toLowerCase()

  // Check basic format
  if (!EMAIL_REGEX.test(trimmedEmail)) {
    return { isValid: false, error: 'Please enter a valid email address format' }
  }

  // Extract domain and TLD
  const parts = trimmedEmail.split('@')
  if (parts.length !== 2) {
    return { isValid: false, error: 'Invalid email format' }
  }

  const domain = parts[1]
  const domainParts = domain.split('.')
  
  if (domainParts.length < 2) {
    return { isValid: false, error: 'Email must include a valid domain' }
  }

  const tld = domainParts[domainParts.length - 1].toLowerCase()

  // Check TLD
  if (!VALID_TLDS.includes(tld)) {
    return {
      isValid: false,
      error: `Invalid domain extension. Please use a valid extension like .com, .org, .pk, .co, etc.`,
    }
  }

  // Check if it's a known provider
  const isKnownProvider = VALID_EMAIL_PROVIDERS.some((provider) => {
    if (provider === 'custom') return false
    return domain === provider
  })

  // If not a known provider, validate custom domain more strictly
  if (!isKnownProvider) {
    const domainWithoutTld = domainParts.slice(0, -1).join('.')
    
    // Check domain name length (should be at least 2 characters)
    if (!domainWithoutTld || domainWithoutTld.length < 2) {
      return {
        isValid: false,
        error: 'Invalid domain name. Domain must be at least 2 characters long.',
      }
    }

    // Check for valid domain structure (alphanumeric and hyphens only, proper format)
    const domainNamePattern = /^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?(\.[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?)*$/i
    if (!domainNamePattern.test(domainWithoutTld)) {
      return {
        isValid: false,
        error: 'Invalid domain name format. Please use a valid email provider or legitimate domain.',
      }
    }

    // Check for suspicious patterns (random character strings)
    // Reject domains that look like random character combinations
    const hasVowel = /[aeiou]/i.test(domainWithoutTld)
    const isCommonPattern = /^(mail|email|web|www|site|online|net|host|server|domain|company|corp|org|business|enterprise|group|team|office|corporate|my|get|go|app|dev|tech|info|blog|shop|store|news|media|digital|global|world|local|city|town|nation|country|state|region|zone|space|hub|center|centre|base|core|main|prime|first|top|best|new|old|big|small|fast|quick|smart|easy|pro|plus|max|min|ultra|super|mega|micro|mini|maxi|mega|giga|tera|peta|exa|zetta|yotta|kilo|milli|nano|pico|femto|atto|zepto|yocto)/i.test(domainWithoutTld)
    
    // Check for patterns that indicate random strings:
    // 1. Short domains (less than 4 chars) without common patterns
    // 2. Domains with unusual consonant clusters (3+ consecutive consonants)
    // 3. Domains that don't look like real words or business names
    
    const hasUnusualConsonantCluster = /[bcdfghjklmnpqrstvwxyz]{3,}/i.test(domainWithoutTld)
    const isVeryShort = domainWithoutTld.length < 4
    
    // Reject if it's very short and not a common pattern
    if (isVeryShort && !isCommonPattern) {
      return {
        isValid: false,
        error: 'Please use a valid email provider (like Gmail, Yahoo, Hotmail, Outlook) or a legitimate business domain.',
      }
    }
    
    // For custom domains shorter than 8 characters, require they match common patterns
    // This prevents random-looking domains like "ksreoe", "xcvbn", etc.
    if (domainWithoutTld.length < 8 && !isCommonPattern) {
      // Check for unusual consonant clusters (3+ consecutive consonants)
      // This catches patterns like "ksr", "xcv", "qwerty" etc.
      if (hasUnusualConsonantCluster) {
        return {
          isValid: false,
          error: 'Please use a valid email provider (like Gmail, Yahoo, Hotmail, Outlook) or a legitimate business domain.',
        }
      }
      
      // Also reject if it doesn't have vowels (all consonants)
      if (!hasVowel) {
        return {
          isValid: false,
          error: 'Please use a valid email provider (like Gmail, Yahoo, Hotmail, Outlook) or a legitimate business domain.',
        }
      }
    }
  }

  return { isValid: true, error: null }
}

/**
 * Yup validation method for email
 * Can be used with yup.string().test('email-validation', ...)
 */
export const yupEmailValidation = (value) => {
  if (!value) return false
  const result = validateEmail(value)
  return result.isValid
}

/**
 * Get error message for email validation
 */
export const getEmailErrorMessage = (value) => {
  if (!value) return 'Email is required'
  const result = validateEmail(value)
  return result.error || 'Please enter a valid email address'
}

/**
 * Yup schema helper for email validation
 * Note: Import yup in the file where you use this function
 * Example: import * as yup from 'yup'
 */
export const createEmailSchema = (yup, required = true) => {
  let schema = yup.string()
  
  if (required) {
    schema = schema.required('Email is required')
  }
  
  return schema
    .test('email-format', 'Please enter a valid email address format', (value) => {
      if (!value) return !required
      return EMAIL_REGEX.test(value.trim().toLowerCase())
    })
    .test('email-domain', (value) => {
      if (!value) return !required
      return getEmailErrorMessage(value)
    }, (value) => {
      if (!value) return !required
      return validateEmail(value).isValid
    })
    .label('Email')
}

// Export constants for use in other files if needed
export { VALID_EMAIL_PROVIDERS, VALID_TLDS, EMAIL_REGEX }

